import {
  IWidgetComponentPropsConfig,
  PdfVisibilityDirectiveKind,
  Widget,
  WidgetComponentContextProvider,
  WidgetRepository,
  extractPdfVisibility,
  getWidgetPath,
  useWidgetScopeContext,
} from '@framework/engine';
import React, { useMemo } from 'react';
import { InputContainer } from './InputContainer';

export interface IFormComponentProps {
  widget: Widget;
  children?: React.ReactNode;
  componentLayout?: IWidgetComponentPropsConfig;
  /**
   * Rendering in modalità readonly (riepilogo, integrazioni, ...)
   */
  readonly?: boolean;
}

/**
 * Componente che, dato un elemento, ne restituisce il componente di form
 * recuperandolo dal widgetRepository.
 */
export const FormComponent = React.memo(
  (props: IFormComponentProps) => {
    const { widget, children, componentLayout, readonly } = props;
    const widgetComponent = WidgetRepository.find(widget.type);
    // Recupero il prefisso dal contesto, generato da alcuni widget (es. repeatable)
    const { prefix } = useWidgetScopeContext();

    const isCompilationVisible = useMemo(() => {
      const visibility = extractPdfVisibility(widget);
      if (!visibility) return true;
      return visibility.visibility !== PdfVisibilityDirectiveKind.PDF;
    }, [widget]);

    if (!widgetComponent) {
      console.warn(`No widget found for type ${widget.type}`, {widget}); // prettier-ignore
      return null;
    }

    /**
     * Se il widget è indicato come solo visibile nel pdf, non viene renderizzato.
     */
    if (!isCompilationVisible) {
      return null;
    }

    /**
     * Scelta del componente da renderizzare. Può essere readonly (per
     * il riepilogo) o form (per la compilazione).
     * Se readonlyComponent non è definito, viene utilizzato il formComponent
     * a cui viene passato il parametro `readonly`, in caso le modifiche siano
     * minime.
     */
    const RenderingComponent =
      readonly && widgetComponent.readonlyComponent
        ? widgetComponent.readonlyComponent
        : widgetComponent.formComponent;

    const widgetFormName = getWidgetPath(widget, prefix);

    return (
      <InputContainer>
        <WidgetComponentContextProvider name={widgetFormName}>
          <RenderingComponent
            widget={widget}
            children={children}
            componentLayout={componentLayout}
            name={widgetFormName}
            readonly={readonly}
          />
        </WidgetComponentContextProvider>
      </InputContainer>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.widget === nextProps.widget &&
      prevProps.readonly === nextProps.readonly
    );
  },
);

FormComponent.displayName = 'FormComponent';
